import { getI18n } from "react-i18next";
import { cleanNotifications, showNotification } from "@mantine/notifications";
import * as Sentry from "@sentry/react";
import { distinct, GraphQLRequestError } from "src/graphql/utils/utils";
import { TranslationKeys } from "src/types/i18next";
import { scrubSensitive } from "src/utils/scrub";

interface ErrorInfo {
    name: string;
    message: string;
    errorKind?: string;
}

export const extractErrorInfo = (error: unknown): ErrorInfo => {
    if (typeof error === "string") {
        return { name: "Unknown error", message: error, errorKind: "unknown" };
    }

    let errorKind: string | undefined;

    if (error instanceof GraphQLRequestError) {
        errorKind = "GraphQLRequestError";
    }

    if (error instanceof Error) {
        return { name: error.name, message: error.message, errorKind: errorKind || "Error" };
    }

    try {
        return { name: "Unknown error", message: JSON.stringify(scrubSensitive(error)), errorKind: "unknown" };
    } catch {
        return { name: "Unknown error", message: "Unknown error", errorKind: "unknown" };
    }
};

type Context = Record<string, string | number | boolean | null | undefined>;

export interface Translation {
    key: TranslationKeys;
    namespace: string;
    params?: Record<string, string | number | boolean | null | undefined>;
}

interface Meta {
    title?: Translation;
    message?: Translation;
    additionalMessage?: string;
    requestId?: string | null;
    context?: Context;
    // The message's {{code}} comes from the Sentry event id (the failure never reached the server, so
    // there is no request id). The id is made before the capture and tagged `supportCode`, otherwise
    // support could not search for the code the client reads out.
    supportCodeFromEvent?: boolean;
}

/**
 * The short code a client can read out to support: eight characters of an AppSync request id or a
 * Sentry event id. Support finds the failure by searching Sentry for the `supportCode` tag, which is
 * set in both cases (from the request id, or from the event id made before the capture). Only a
 * browser with no Web Crypto at all leaves the tag off; support then searches for the event id.
 */
export const toSupportCode = (id: string) => id.slice(0, 8).toUpperCase();

// A 32-character hex event id made before the capture. `crypto.randomUUID` is missing in Safari < 15.4,
// Chrome < 92, Firefox < 95 and insecure contexts, so fall back to `getRandomValues`; with neither,
// return undefined and let Sentry generate the id.
const makeEventId = (): string | undefined => {
    const webCrypto = globalThis.crypto;
    if (typeof webCrypto?.randomUUID === "function") {
        return webCrypto.randomUUID().replace(/-/g, "");
    }
    if (typeof webCrypto?.getRandomValues === "function") {
        return Array.from(webCrypto.getRandomValues(new Uint8Array(16)), (byte) =>
            byte.toString(16).padStart(2, "0")
        ).join("");
    }
    return undefined;
};

// TTV2-1919: Sentry is off outside production builds (sentry.ts `enabled`), so off prod a handled
// error left nothing to read but the network tab. Every build whose environment is not prod —
// lanes, dev, stag and `vite dev` — prints one console line per handled error instead. The gate is
// the environment Sentry tags events with (VITE_ENVIRONMENT, sentry.ts), not Sentry's `enabled`
// flag: that is NODE_ENV=production, which every deployed bundle has, lanes included.
const printsConsoleLine = () => import.meta.env.VITE_ENVIRONMENT !== "prod";

const MAX_CONSOLE_MESSAGE = 500;

// Best effort: server and library messages rarely carry personal data, but a Cognito or fetch
// message can echo an e-mail or a token. The context, the variables and request bodies are never
// printed at all.
const scrubText = (text: string) =>
    text
        .replace(/Bearer\s+\S+/gi, "Bearer [redacted]")
        .replace(/eyJ[A-Za-z0-9_-]{10,}(\.[A-Za-z0-9_-]+){0,2}/g, "[redacted]")
        .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]")
        .replace(/\+\d[\d ]{7,}\d|\b\d{3} \d{3} \d{3}\b/g, "[phone]")
        .replace(/\s+/g, " ")
        .trim();

/** One key=value field: scrubbed, no whitespace, "-" when empty. */
const consoleField = (value: string | null | undefined) => scrubText(value ?? "").replace(/ /g, "_") || "-";

const consoleMessage = (text: string) => {
    const scrubbed = scrubText(text);
    return scrubbed.length > MAX_CONSOLE_MESSAGE ? `${scrubbed.slice(0, MAX_CONSOLE_MESSAGE)}…` : scrubbed;
};

/** `[tt-error] op=… type=… path=… requestId=… msg=…` for one handled error. */
export const describeHandledError = (error: unknown, requestId?: string | null): string => {
    if (error instanceof GraphQLRequestError) {
        const errors = error.errorsNormalized;
        return [
            "[tt-error]",
            `op=${consoleField(error.operationName || "unknown")}`,
            `type=${consoleField(distinct(errors.map((e) => e.errorType)).join(","))}`,
            `path=${consoleField(distinct(errors.map((e) => e.path)).join(","))}`,
            `requestId=${consoleField(error.requestId || requestId)}`,
            `msg=${consoleMessage(error.messages)}`,
        ].join(" ");
    }

    const { message, errorKind } = extractErrorInfo(error);
    return [
        "[tt-error]",
        "op=-",
        `type=${consoleField(error instanceof Error ? error.name : errorKind)}`,
        "path=-",
        `requestId=${consoleField(requestId)}`,
        `msg=${consoleMessage(message)}`,
    ].join(" ");
};

export const reportError = (error: unknown, meta: Meta) => {
    if (printsConsoleLine()) {
        console.error(describeHandledError(error, meta.requestId));
    }

    const { name, message, errorKind } = extractErrorInfo(error);
    const errorName = meta.title ? `${meta.title.namespace}:${meta.title.key}` : name;
    const errorMessage = meta.message ? `${meta.message.namespace}:${meta.message.key}` : message;
    let requestId: string | undefined | null = meta.requestId;
    let eventId = "";
    const presetEventId = meta.supportCodeFromEvent ? makeEventId() : undefined;

    Sentry.withScope((scope) => {
        scope.setTag("errorSource", "handledError");
        scope.setTag("errorName", errorName);
        scope.setTag("errorKind", errorKind);
        scope.setExtra("errorMessage", errorMessage);

        if (meta?.additionalMessage) {
            scope.setExtra("additionalMessage", meta.additionalMessage);
        }

        if (error instanceof GraphQLRequestError) {
            scope.setContext(
                "graphql",
                scrubSensitive({
                    operationType: error.operationType,
                    operationName: error.operationName,
                    normalizedErrors: error.errorsNormalized.map((err) => err.message).join(", "),
                    variablesKeys: error.variablesKeys,
                })
            );

            requestId = error.requestId;
            if (error.fingerprint?.length) {
                scope.setFingerprint(error.fingerprint as string[]);
            }
        }

        if (requestId) {
            scope.setExtra("requestId", requestId);
            scope.setTag("requestId", requestId);
        }

        const supportCode = presetEventId ? toSupportCode(presetEventId) : meta.message?.params?.code;
        if (supportCode) {
            scope.setTag("supportCode", String(supportCode));
        }

        const context = scrubSensitive(meta?.context);
        if (context && typeof context === "object") {
            for (const [key, value] of Object.entries(context)) {
                scope.setExtra(key, value as unknown);
            }
        }

        eventId = Sentry.captureException(error, presetEventId ? { event_id: presetEventId } : undefined);
    });

    return eventId;
};

export const handleError = (
    error: unknown,
    options?: Partial<Meta> & {
        silent?: boolean;
        autoClose?: boolean;
    }
) => {
    const { t } = getI18n();
    const {
        silent,
        autoClose = true,
        supportCodeFromEvent,
        message = { key: "error_message", namespace: "common" },
        title = { key: "error", namespace: "common" },
        additionalMessage,
        ...restMeta
    } = options ?? {};

    const eventId = reportError(error, { title, message, additionalMessage, supportCodeFromEvent, ...restMeta });

    const params = supportCodeFromEvent ? { ...message.params, code: toSupportCode(eventId) } : message.params;
    const messageText =
        t(message.key, { ns: message.namespace, ...params }) + (additionalMessage ? `: ${additionalMessage}` : "");
    const titleText = t(title.key, { ns: title.namespace, ...title.params });

    if (!silent) {
        cleanNotifications();
        showNotification({ color: "red", title: titleText, message: messageText, autoClose });
    }
    return error;
};
